import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  getCountFromServer,
  Timestamp,
  documentId,
  deleteField,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirestorePageResult } from '@/hooks/useFirestorePagination'
import { db, COLLECTIONS } from '@/lib/firebase'
import {
  buildProductSearchFields,
  parseSearchQuery,
  productMatchesSearchTokens,
} from '@/lib/productSearchFields'
import type { Product } from '@/types'
import { parseProductAudience } from '@/lib/productAudience'

const col = collection(db, COLLECTIONS.products)

function mapProduct(id: string, data: Record<string, unknown>): Product {
  return {
    id,
    name: (data.name as string) || '',
    subtitle: data.subtitle as string | undefined,
    description: (data.description as string) || '',
    fragranceNotes: data.fragranceNotes as string | undefined,
    category: (data.category as string) || 'Attar',
    allowCustomization: data.allowCustomization === true,
    noteIds: Array.isArray(data.noteIds)
      ? (data.noteIds as string[]).filter((id) => typeof id === 'string' && id)
      : undefined,
    price: Number(data.price) || 0,
    discountPrice: data.discountPrice ? Number(data.discountPrice) : undefined,
    quantity: Number(data.quantity) || 0,
    status: (data.status as Product['status']) || 'active',
    sku: (data.sku as string) || '',
    images: (data.images as string[]) || [],
    sizes: data.sizes as Product['sizes'],
    featured: Boolean(data.featured),
    royal: data.royal === true,
    audience: parseProductAudience(data.audience),
    rating: data.rating != null ? Number(data.rating) : undefined,
    ratingCount: data.ratingCount != null ? Number(data.ratingCount) : undefined,
    lowStockThreshold: Number(data.lowStockThreshold) || 5,
    createdAt: (data.createdAt as string) || new Date().toISOString(),
    updatedAt: (data.updatedAt as string) || new Date().toISOString(),
  }
}

type SizeVariant = NonNullable<Product['sizes']>[number]

/** Firestore rejects `undefined` field values. */
function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>
}

function cleanSizeVariants(sizes: Product['sizes']): Product['sizes'] {
  if (!sizes?.length) return sizes
  return sizes.map((variant) => {
    const entries = Object.entries(variant).filter(([, value]) => value !== undefined)
    return Object.fromEntries(entries) as SizeVariant
  })
}

/** Strip review aggregates and undefined values from admin product writes. */
function buildProductWritePayload(
  data: Partial<Product>
): Record<string, unknown> {
  const {
    id: _id,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    rating: _rating,
    ratingCount: _ratingCount,
    audience: _audience,
    noteIds: _noteIds,
    sizes,
    ...rest
  } = data

  return omitUndefined({
    ...rest,
    ...(sizes !== undefined ? { sizes: cleanSizeVariants(sizes) } : {}),
  })
}

export type ProductCategoryFilter = string

function buildProductsQuery(category: ProductCategoryFilter = 'all') {
  if (category !== 'all') {
    return query(
      col,
      where('category', '==', category),
      orderBy('createdAt', 'desc')
    )
  }
  return query(col, orderBy('createdAt', 'desc'))
}

export async function countProducts(
  category: ProductCategoryFilter = 'all'
): Promise<number> {
  const snap = await getCountFromServer(buildProductsQuery(category))
  return snap.data().count
}

export async function fetchProductsPage(opts: {
  pageSize: number
  startAfter: QueryDocumentSnapshot | null
  category?: ProductCategoryFilter
}): Promise<FirestorePageResult<Product>> {
  const base = buildProductsQuery(opts.category ?? 'all')
  const q =
    opts.startAfter != null
      ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
      : query(base, limit(opts.pageSize))
  const snap = await getDocs(q)
  const items = snap.docs.map((d) => mapProduct(d.id, d.data()))
  const lastDoc =
    snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
  return {
    items,
    lastDoc,
    hasMore: snap.docs.length === opts.pageSize,
  }
}

export async function fetchProducts(): Promise<Product[]> {
  const snap = await getDocs(buildProductsQuery('all'))
  return snap.docs.map((d) => mapProduct(d.id, d.data()))
}

/** Active products for storefront */
export async function fetchStoreProducts(): Promise<Product[]> {
  const products = await fetchProducts()
  return products.filter((p) => p.status === 'active')
}

export async function fetchFeaturedStoreProducts(maxItems = 4): Promise<Product[]> {
  const q = query(
    col,
    where('status', '==', 'active'),
    where('featured', '==', true),
    orderBy('createdAt', 'desc'),
    limit(Math.max(1, maxItems))
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => mapProduct(d.id, d.data()))
}

export async function fetchNewArrivalStoreProducts(maxItems = 4): Promise<Product[]> {
  const q = query(activeStoreProductsQuery(), limit(Math.max(1, maxItems)))
  const snap = await getDocs(q)
  return snap.docs.map((d) => mapProduct(d.id, d.data()))
}

/** Same-category storefront products for the detail page (no full-catalog fetch). */
export async function fetchSimilarStoreProducts(
  category: string,
  excludeId: string,
  maxItems = 4
): Promise<Product[]> {
  const q = query(
    col,
    where('category', '==', category),
    orderBy('createdAt', 'desc'),
    limit(Math.max(maxItems + 2, 6))
  )
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => mapProduct(d.id, d.data()))
    .filter((p) => p.status === 'active' && p.id !== excludeId)
    .slice(0, maxItems)
}

export async function fetchStoreProductsByIds(ids: string[]): Promise<Product[]> {
  const unique = [...new Set(ids.filter(Boolean))]
  if (unique.length === 0) return []

  const batches: string[][] = []
  for (let i = 0; i < unique.length; i += 30) {
    batches.push(unique.slice(i, i + 30))
  }

  const maps = await Promise.all(
    batches.map(async (batch) => {
      const snap = await getDocs(
        query(col, where(documentId(), 'in', batch))
      )
      return snap.docs.map((d) => mapProduct(d.id, d.data()))
    })
  )

  const byId = new Map(maps.flat().map((p) => [p.id, p]))
  return unique
    .map((id) => byId.get(id))
    .filter((p): p is Product => Boolean(p))
}

function activeStoreProductsQuery() {
  return query(
    col,
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc')
  )
}

function activeStoreProductsSearchQuery(searchTokens: string[]) {
  const base = query(col, where('status', '==', 'active'))
  if (searchTokens.length === 1) {
    return query(
      base,
      where('searchTokens', 'array-contains', searchTokens[0]),
      orderBy('createdAt', 'desc')
    )
  }
  return query(
    base,
    where('searchTokens', 'array-contains-any', searchTokens.slice(0, 10)),
    orderBy('createdAt', 'desc')
  )
}

export async function countActiveStoreProducts(): Promise<number> {
  const snap = await getCountFromServer(activeStoreProductsQuery())
  return snap.data().count
}

export async function countActiveStoreProductsSearch(q: string): Promise<number> {
  const tokens = parseSearchQuery(q)
  if (tokens.length === 0) return 0
  const snap = await getCountFromServer(activeStoreProductsSearchQuery(tokens))
  return snap.data().count
}

export async function fetchActiveStoreProductsPage(opts: {
  pageSize: number
  startAfter: QueryDocumentSnapshot | null
}): Promise<FirestorePageResult<Product>> {
  const base = activeStoreProductsQuery()
  const q =
    opts.startAfter != null
      ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
      : query(base, limit(opts.pageSize))
  const snap = await getDocs(q)
  const items = snap.docs.map((d) => mapProduct(d.id, d.data()))
  const lastDoc =
    snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
  return {
    items,
    lastDoc,
    hasMore: snap.docs.length === opts.pageSize,
  }
}

export async function fetchActiveStoreProductsSearchPage(
  q: string,
  opts: {
    pageSize: number
    startAfter: QueryDocumentSnapshot | null
  }
): Promise<FirestorePageResult<Product>> {
  const tokens = parseSearchQuery(q)
  if (tokens.length === 0) {
    return { items: [], lastDoc: null, hasMore: false }
  }

  const base = activeStoreProductsSearchQuery(tokens)
  const queryRef =
    opts.startAfter != null
      ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
      : query(base, limit(opts.pageSize))
  const snap = await getDocs(queryRef)
  const items = snap.docs.map((d) => mapProduct(d.id, d.data()))
  const lastDoc =
    snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
  return {
    items,
    lastDoc,
    hasMore: snap.docs.length === opts.pageSize,
  }
}

export const SHOP_STORE_PAGE_SIZE = 15

export type ShopStoreProductsSort = 'default' | 'new' | 'price-asc' | 'price-desc'

export type ShopStoreProductsFilters = {
  query?: string
  category?: string
  featuredOnly?: boolean
  noteIds?: string[]
  sort?: ShopStoreProductsSort
}

function shopSearchTokens(filters: ShopStoreProductsFilters): string[] {
  return parseSearchQuery(filters.query ?? '')
}

function shopNoteIds(filters: ShopStoreProductsFilters): string[] {
  return (filters.noteIds ?? []).filter(Boolean)
}

/** Firestore allows one array filter — search takes priority over notes. */
function shopUsesClientNoteRefinement(filters: ShopStoreProductsFilters): boolean {
  return shopSearchTokens(filters).length > 0 && shopNoteIds(filters).length > 0
}

function buildShopStoreProductsQuery(filters: ShopStoreProductsFilters) {
  const constraints: QueryConstraint[] = [where('status', '==', 'active')]

  const category = filters.category?.trim()
  if (category) {
    constraints.push(where('category', '==', category))
  }
  if (filters.featuredOnly) {
    constraints.push(where('featured', '==', true))
  }

  const tokens = shopSearchTokens(filters)
  const noteIds = shopNoteIds(filters)

  if (tokens.length === 1) {
    constraints.push(where('searchTokens', 'array-contains', tokens[0]))
  } else if (tokens.length > 1) {
    constraints.push(
      where('searchTokens', 'array-contains-any', tokens.slice(0, 10))
    )
  } else if (noteIds.length === 1) {
    constraints.push(where('noteIds', 'array-contains', noteIds[0]))
  } else if (noteIds.length > 1) {
    constraints.push(where('noteIds', 'array-contains-any', noteIds.slice(0, 10)))
  }

  const sort = filters.sort ?? 'default'
  if (sort === 'price-asc') {
    constraints.push(orderBy('price', 'asc'))
  } else if (sort === 'price-desc') {
    constraints.push(orderBy('price', 'desc'))
  } else {
    constraints.push(orderBy('createdAt', 'desc'))
  }

  return query(col, ...constraints)
}

function refineShopProductsForClientFilters(
  items: Product[],
  filters: ShopStoreProductsFilters
): Product[] {
  if (!shopUsesClientNoteRefinement(filters)) return items
  const noteIds = shopNoteIds(filters)
  return items.filter((p) =>
    noteIds.some((noteId) => p.noteIds?.includes(noteId))
  )
}

export async function countShopStoreProducts(
  filters: ShopStoreProductsFilters
): Promise<number> {
  const snap = await getCountFromServer(buildShopStoreProductsQuery(filters))
  return snap.data().count
}

export async function fetchShopStoreProductsPage(
  filters: ShopStoreProductsFilters,
  opts: {
    pageSize: number
    startAfter: QueryDocumentSnapshot | null
  }
): Promise<FirestorePageResult<Product>> {
  const base = buildShopStoreProductsQuery(filters)
  const needsRefinement = shopUsesClientNoteRefinement(filters)

  if (!needsRefinement) {
    const q =
      opts.startAfter != null
        ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
        : query(base, limit(opts.pageSize))
    const snap = await getDocs(q)
    const items = snap.docs.map((d) => mapProduct(d.id, d.data()))
    const lastDoc =
      snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
    return {
      items,
      lastDoc,
      hasMore: snap.docs.length === opts.pageSize,
    }
  }

  let cursor = opts.startAfter
  let lastDoc: QueryDocumentSnapshot | null = null
  let hasMore = true
  const collected: Product[] = []
  const batchSize = opts.pageSize
  let guard = 0

  while (collected.length < opts.pageSize && hasMore && guard < 8) {
    guard += 1
    const q =
      cursor != null
        ? query(base, startAfter(cursor), limit(batchSize))
        : query(base, limit(batchSize))
    const snap = await getDocs(q)
    hasMore = snap.docs.length === batchSize
    lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : lastDoc
    cursor = lastDoc

    const refined = refineShopProductsForClientFilters(
      snap.docs.map((d) => mapProduct(d.id, d.data())),
      filters
    )
    collected.push(...refined)

    if (snap.docs.length === 0) {
      hasMore = false
    }
  }

  return {
    items: collected.slice(0, opts.pageSize),
    lastDoc,
    hasMore,
  }
}

export async function fetchShopFilterCounts(input: {
  categories: { name: string }[]
  notes: { id: string }[]
  query?: string
  featuredOnly?: boolean
  category?: string
  selectedNoteIds?: string[]
}): Promise<{
  categoryCounts: Record<string, number>
  noteCounts: Record<string, number>
  totalAll: number
}> {
  const shared = {
    query: input.query,
    featuredOnly: input.featuredOnly,
    sort: 'default' as ShopStoreProductsSort,
  }

  const categoryBase: ShopStoreProductsFilters = {
    ...shared,
    noteIds: input.selectedNoteIds?.filter(Boolean),
  }

  const noteBase: ShopStoreProductsFilters = {
    ...shared,
    category: input.category?.trim() || undefined,
  }

  const [totalAll, categoryPairs, notePairs] = await Promise.all([
    countShopStoreProducts(categoryBase),
    Promise.all(
      input.categories.map(async (cat) => {
        const count = await countShopStoreProducts({
          ...categoryBase,
          category: cat.name,
        })
        return [cat.name, count] as const
      })
    ),
    Promise.all(
      input.notes.map(async (note) => {
        const count = await countShopStoreProducts({
          ...noteBase,
          noteIds: [note.id],
        })
        return [note.id, count] as const
      })
    ),
  ])

  return {
    totalAll,
    categoryCounts: Object.fromEntries(categoryPairs),
    noteCounts: Object.fromEntries(notePairs),
  }
}

/** Search active storefront products (client-side filter; works without searchTokens). */
export async function searchActiveStoreProducts(q: string): Promise<Product[]> {
  const tokens = parseSearchQuery(q)
  if (tokens.length === 0) return []

  let products: Product[] = []
  try {
    const snap = await getDocs(activeStoreProductsSearchQuery(tokens))
    products = snap.docs.map((d) => mapProduct(d.id, d.data()))
    products = products.filter((p) => productMatchesSearchTokens(p, tokens))
  } catch {
    products = []
  }

  if (products.length === 0) {
    const snap = await getDocs(activeStoreProductsQuery())
    products = snap.docs
      .map((d) => mapProduct(d.id, d.data()))
      .filter((p) => productMatchesSearchTokens(p, tokens))
  }

  return products.sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

export async function fetchProduct(id: string): Promise<Product | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.products, id))
  if (!snap.exists()) return null
  return mapProduct(snap.id, snap.data())
}

export async function createProduct(
  data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString()
  const { audience, noteIds } = data
  const ref = await addDoc(col, {
    ...buildProductWritePayload(data),
    ...(audience ? { audience } : {}),
    ...(noteIds?.length ? { noteIds } : {}),
    ...buildProductSearchFields(data),
    createdAt: now,
    updatedAt: now,
    createdAtTs: Timestamp.now(),
  })
  return ref.id
}

export async function updateProduct(
  id: string,
  data: Partial<Product>
): Promise<void> {
  const { audience, noteIds, ...rest } = data
  let searchFields: ReturnType<typeof buildProductSearchFields> | undefined

  const textFields = [
    'name',
    'subtitle',
    'description',
    'fragranceNotes',
    'category',
    'sku',
  ] as const
  if (textFields.some((field) => field in rest)) {
    const existing = await fetchProduct(id)
    if (existing) {
      searchFields = buildProductSearchFields({ ...existing, ...rest })
    }
  }

  const audienceUpdate =
    'audience' in data
      ? audience
        ? { audience }
        : { audience: deleteField() }
      : {}

  const noteIdsUpdate =
    'noteIds' in data
      ? noteIds?.length
        ? { noteIds }
        : { noteIds: deleteField() }
      : {}

  await updateDoc(doc(db, COLLECTIONS.products, id), {
    ...buildProductWritePayload(rest),
    ...searchFields,
    ...audienceUpdate,
    ...noteIdsUpdate,
    updatedAt: new Date().toISOString(),
  })
}

export async function deleteProduct(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.products, id))
}
